/**
 * WeeklyQuestCard — Iter. 406.n + Iter. 406.fr.3 (interactive modal)
 *
 * Dashboard widget showing the 7-day quest with day-by-day
 * checkboxes, progress bar, and points cumulated this week. When the
 * user toggles the 7th day, the backend awards the J7 bonus + flags a
 * pending Orion celebration, picked up by OrionSymbioseModal.
 *
 * Each day row is now CLICKABLE -> opens an educational modal. The
 * modal shows the 2-3 sentence `educational_description` and exposes
 * one of two actions based on `action_type`:
 *   - "link"     : button that navigates to `link_target` (/meals, ...)
 *   - "embedded" : in-modal "Comment faire" guide + "Marquer comme
 *                  complete" button that calls /weekly-quest/complete-day.
 *
 * The card auto-fetches the current quest on mount; pull-to-refresh on
 * the Dashboard refetches it.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Sparkles, Loader2, CalendarDays, X, ArrowRight, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../context/AppContext';

const DAY_LABELS_FR = ['LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM', 'DIM'];
const DAY_LABELS_EN = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

const CATEGORY_TINT = {
  vitalite_mouvement: 'from-emerald-500/30 to-emerald-700/15',
  nutrition_carburant: 'from-orange-500/30 to-orange-700/15',
  recuperation_optimale: 'from-sky-500/30 to-sky-700/15',
  force_mentale: 'from-violet-500/30 to-violet-700/15',
  sommeil_equilibre: 'from-indigo-500/30 to-indigo-700/15',
};

const CATEGORY_ICON = {
  vitalite_mouvement: '💪',
  nutrition_carburant: '🍎',
  recuperation_optimale: '💧',
  force_mentale: '🧠',
  sommeil_equilibre: '💤',
};

export default function WeeklyQuestCard({ language = 'fr', onJ7Completed }) {
  const fr = language === 'fr';
  const dayLabels = fr ? DAY_LABELS_FR : DAY_LABELS_EN;
  const navigate = useNavigate();
  const [quest, setQuest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingDay, setPendingDay] = useState(null);
  // Iter. 406.fr.3 — Interactive educational modal
  const [activeDay, setActiveDay] = useState(null); // the day-dict the user tapped

  // Iter. 411.mm16 — SAFETY-NET ROLLBACK BUG FIX.
  //
  // Iter. 411.mm12 added a `setTimeout(loadQuest, 400)` safety refetch after
  // every successful toggle. On production MongoDB the read-your-write lag
  // can exceed 400 ms, so the refetch sometimes hit a replica that hadn't
  // received the write yet → returned the OLD quest (day NOT completed) →
  // `setQuest(oldQuest)` **rolled the UI back**, giving users the illusion
  // of a rollback ("cliquée puis se décoche toute seule après 3s"). Same
  // trap for the `focus`/`visibilitychange` listeners.
  //
  // Fix — MERGE-SAFE loadQuest:
  // Refetch fine, but if a day was already marked completed locally
  // (either because we just toggled it OR the server told us it was),
  // NEVER downgrade it back to incomplete. Only the "opposite direction"
  // (server has MORE completed than us) can overwrite. This preserves
  // read-your-write consistency for the user without needing to disable
  // the refetch entirely (which we still want for cross-device sync).
  // Iter. 411.mm21 (OPTION MOYEN) — debounced + skip-if-recent so a
  // burst of visibilitychange events (e.g. Android exiting a fullscreen
  // video) collapses into one call, and we never fire within 10 s of a
  // prior successful fetch. Matches the AuthContext guard semantics.
  const lastLoadRef = React.useRef(0);
  const LOAD_COOLDOWN_MS = 10000;
  const loadQuest = useCallback(async (opts = {}) => {
    const now = Date.now();
    if (!opts.force && now - lastLoadRef.current < LOAD_COOLDOWN_MS) {
      return;
    }
    try {
      const res = await api.get('/weekly-quest/current');
      const data = res?.data;
      if (!data || !Array.isArray(data.days)) return;
      lastLoadRef.current = Date.now();

      setQuest((prev) => {
        if (!prev || !Array.isArray(prev.days)) return data;

        // Build a lookup of the day-level state we currently hold. If a
        // day is completed locally, keep it completed even if the server
        // response says otherwise (replica lag).
        const prevByIdx = new Map(prev.days.map((d) => [d.day_index, d]));
        const mergedDays = data.days.map((serverDay) => {
          const local = prevByIdx.get(serverDay.day_index);
          if (local?.completed && !serverDay.completed) {
            // Replica lag: server hasn't seen the recent write yet. Keep
            // the local completed flag + the local completed_at
            // timestamp, but otherwise take the server's canonical fields
            // (title/desc could have changed via admin regeneration).
            return {
              ...serverDay,
              completed: true,
              completed_at: local.completed_at || serverDay.completed_at,
            };
          }
          return serverDay;
        });

        const completedCount = mergedDays.filter((d) => d.completed).length;
        return {
          ...data,
          days: mergedDays,
          completed_days: Math.max(
            data.completed_days || 0,
            prev.completed_days || 0,
            completedCount,
          ),
          total_points_awarded: Math.max(
            data.total_points_awarded || 0,
            prev.total_points_awarded || 0,
          ),
        };
      });
    } catch {
      // Keep the previous state on transient network errors so the user
      // doesn't lose their tick history to a bad Wi-Fi hiccup.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQuest({ force: true });
    // 300 ms debounce so a burst of focus/visibility events (iOS Safari
    // backgrounding, Android fullscreen-exit animation) collapse into
    // one call. loadQuest itself also skips if we fetched within 10 s.
    let debounce = null;
    const schedule = () => {
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(() => { debounce = null; loadQuest(); }, 300);
    };
    const onFocus = () => schedule();
    const onVisibility = () => { if (document.visibilityState === 'visible') schedule(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      if (debounce) clearTimeout(debounce);
    };
  }, [loadQuest]);

  const handleToggle = useCallback(async (dayIndex) => {
    if (pendingDay) return;
    const day = (quest?.days || []).find((d) => d.day_index === dayIndex);
    if (!day || day.completed) return; // server-side idempotency, but UI guard
    setPendingDay(dayIndex);
    try {
      const res = await api.post('/weekly-quest/complete-day', { day_index: dayIndex });
      const payload = res?.data;
      const fresh = payload?.quest;

      // Iter. 411.mm23 — Backend may now return success:false + reason="quest_regenerated"
      // when the quest was rolled over between the user's GET and POST
      // (week rollover edge). In that case we silently refresh — no
      // toast error, because the user did nothing wrong.
      if (payload?.reason === 'quest_regenerated' && fresh && Array.isArray(fresh.days)) {
        setQuest(fresh);
        return;
      }

      // STRICT server-confirmation gate — no optimistic UI. The backend
      // MUST return `success:true` AND the target day flagged completed.
      const confirmed = payload?.success === true
        && fresh
        && Array.isArray(fresh.days)
        && !!fresh.days.find((d) => d.day_index === dayIndex)?.completed;

      if (!confirmed) {
        // Verbose log so we can diagnose the "clicks but doesn't save"
        // reports (Cédric 2026-02-09, W41). Captures exactly WHY the
        // confirmation gate didn't pass.
        try {
          console.warn('[WeeklyQuest] POST not confirmed', {
            success: payload?.success,
            hasFresh: !!fresh,
            daysLen: Array.isArray(fresh?.days) ? fresh.days.length : 'N/A',
            targetDay: dayIndex,
            targetDayInResponse: Array.isArray(fresh?.days)
              ? fresh.days.find((d) => d.day_index === dayIndex)
              : 'N/A',
          });
        } catch { /* noop */ }
        // Bad response — force a fresh GET so the UI mirrors the DB, then
        // tell the user out loud (never fail silent per user directive).
        await loadQuest({ force: true });
        toast.error(fr
          ? "Erreur de sauvegarde — le serveur n'a pas confirmé. Réessaie."
          : language === 'es'
            ? 'Error de guardado — el servidor no confirmó. Reintenta.'
            : "Save error — server didn't confirm. Try again.");
        return;
      }

      setQuest(fresh);
      // Broadcast so sibling widgets (BattlePlanTracker "PLAN D'ATTAQUE",
      // "CETTE SEM." rail) resync instantly.
      try {
        window.dispatchEvent(new CustomEvent('yumix:weekly-quest-updated', {
          detail: { quest: fresh, day_index: dayIndex },
        }));
      } catch { /* non-critical */ }

      // NO safety-net refetch here anymore — it was the root cause of the
      // "case se décoche après 3s" bug on prod. The write is already
      // confirmed by the POST response; a follow-up GET only risks hitting
      // an unreplicated secondary and creating a phantom rollback. The
      // merge-safe loadQuest above already protects the next focus/
      // visibilitychange event.

      if (payload?.j7_celebration && typeof onJ7Completed === 'function') {
        onJ7Completed();
      }
    } catch (err) {
      // Network/auth failure — refetch to stay in sync with the server
      // and surface a CLEAR message per user directive (never fail silent).
      await loadQuest({ force: true });
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const msg = fr
        ? (status === 400 && typeof detail === 'string'
            ? `Erreur de sauvegarde (${detail})`
            : 'Erreur de sauvegarde — vérifie ta connexion.')
        : language === 'es'
          ? (status === 400 && typeof detail === 'string'
              ? `Error de guardado (${detail})`
              : 'Error de guardado — revisa tu conexión.')
          : (status === 400 && typeof detail === 'string'
              ? `Save error (${detail})`
              : "Save error — check your connection.");
      toast.error(msg);
    } finally {
      setPendingDay(null);
    }
  }, [pendingDay, quest, onJ7Completed, loadQuest, fr, language]);

  if (loading && !quest) {
    return (
      <div className="rounded-2xl bg-card border border-border p-5 flex items-center justify-center" data-testid="weekly-quest-loading">
        <Loader2 className="w-5 h-5 text-violet-300 animate-spin" />
      </div>
    );
  }

  if (!quest || !Array.isArray(quest.days)) return null;

  const completed = quest.completed_days ?? quest.days.filter((d) => d.completed).length;
  const progress = (completed / 7) * 100;
  const allDone = completed === 7;

  return (
    <div
      className="relative rounded-2xl bg-gradient-to-br from-amber-500/15 via-violet-500/10 to-emerald-500/10 border-2 border-amber-400/40 shadow-[0_0_30px_rgba(251,191,36,0.18)] overflow-hidden"
      data-testid="weekly-quest-card"
    >
      <div className="pointer-events-none absolute -top-12 -right-12 w-32 h-32 rounded-full bg-amber-500/20 blur-3xl" />

      {/* Header */}
      <div className="relative px-4 pt-4 pb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="shrink-0 w-9 h-9 rounded-xl bg-amber-500/25 border border-amber-300/40 flex items-center justify-center">
            <CalendarDays className="w-4 h-4 text-amber-200" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-amber-200 font-bold flex items-center gap-1" data-testid="weekly-quest-title">
              <Sparkles className="w-3 h-3" />
              {fr ? 'Defi 7 Jours' : '7-Day Quest'} • {quest.week_id}
            </p>
            <h3 className="font-heading uppercase text-sm text-white leading-tight truncate" data-testid="weekly-quest-subtitle">
              {allDone
                ? (fr ? '7/7 — Symbiose scelle' : '7/7 — Symbiosis sealed')
                : (fr ? `${completed}/7 jours valides` : `${completed}/7 days validated`)}
            </h3>
          </div>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-[10px] uppercase tracking-widest text-white/50">
            {fr ? 'Points cumules' : 'Points earned'}
          </p>
          <p className="text-base font-black text-amber-200 leading-none" data-testid="weekly-quest-points">
            +{quest.total_points_awarded || 0}
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-4">
        <div className="h-1.5 rounded-full bg-black/30 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-amber-400 to-amber-600 transition-all duration-700"
            style={{ width: `${progress}%` }}
            data-testid="weekly-quest-progress"
          />
        </div>
      </div>

      {/* Day rows */}
      <ul className="px-4 py-3 space-y-2" data-testid="weekly-quest-days">
        {quest.days.map((d, i) => {
          const tint = CATEGORY_TINT[d.category] || 'from-white/10 to-white/5';
          const icon = CATEGORY_ICON[d.category] || '🎯';
          const isPending = pendingDay === d.day_index;
          return (
            <li
              key={d.day_index}
              className={`relative rounded-xl bg-gradient-to-r ${tint} border ${d.completed ? 'border-amber-300/50' : 'border-white/10'} px-3 py-2.5 flex items-center gap-3 cursor-pointer hover:border-amber-300/70 hover:scale-[1.01] active:scale-[0.99] transition-all`}
              data-testid={`weekly-quest-day-${d.day_index}`}
              onClick={() => setActiveDay(d)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveDay(d); } }}
            >
              <div className="shrink-0 flex flex-col items-center min-w-[2rem]">
                <span className={`text-[9px] uppercase tracking-widest font-bold ${d.completed ? 'text-amber-200' : 'text-white/50'}`}>
                  {dayLabels[i]}
                </span>
                <span className="text-[9px] text-white/40 mt-0.5">J{d.day_index}</span>
              </div>

              <div className="shrink-0 w-8 h-8 rounded-lg bg-black/30 flex items-center justify-center text-lg" aria-hidden="true">
                {icon}
              </div>

              <div className="flex-1 min-w-0">
                <p className={`text-sm font-bold leading-tight truncate ${d.completed ? 'text-white' : 'text-white/85'}`}>
                  {d.title}
                </p>
                <p className="text-[10px] text-white/55 truncate leading-tight">
                  {d.description}
                </p>
              </div>

              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handleToggle(d.day_index); }}
                disabled={d.completed || isPending}
                aria-label={d.completed ? (fr ? 'Defi complete' : 'Quest completed') : (fr ? 'Marquer comme complete' : 'Mark complete')}
                data-testid={`weekly-quest-toggle-${d.day_index}`}
                className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                  d.completed
                    ? 'bg-amber-400 text-black'
                    : isPending
                      ? 'bg-white/10 text-white/50'
                      : 'bg-white/10 hover:bg-amber-400/40 text-white/70'
                }`}
              >
                {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              </button>
            </li>
          );
        })}
      </ul>

      {allDone && (
        <div className="relative px-4 py-2 border-t border-amber-300/30 bg-amber-500/10 text-center" data-testid="weekly-quest-sealed">
          <p className="text-[10px] uppercase tracking-widest font-bold text-amber-100">
            {fr
              ? '✨ Symbiose Orion activee — bonus +250 a +550 pts ✨'
              : '✨ Orion Symbiosis activated — bonus +250 to +550 pts ✨'}
          </p>
        </div>
      )}

      {/* Iter. 406.fr.3 — Educational/Action modal */}
      {activeDay && (
        <DayDetailModal
          day={activeDay}
          fr={fr}
          icon={CATEGORY_ICON[activeDay.category] || '🎯'}
          tint={CATEGORY_TINT[activeDay.category] || 'from-white/10 to-white/5'}
          pending={pendingDay === activeDay.day_index}
          onClose={() => setActiveDay(null)}
          onLink={(target) => {
            setActiveDay(null);
            if (target) navigate(target);
          }}
          onMarkComplete={async () => {
            await handleToggle(activeDay.day_index);
            setActiveDay(null);
          }}
        />
      )}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────────────
// Iter. 406.fr.3 — DayDetailModal (educational + action modal)
// ──────────────────────────────────────────────────────────────────────────
function DayDetailModal({ day, fr, icon, tint, pending, onClose, onLink, onMarkComplete }) {
  const actionType = day.action_type === 'embedded' ? 'embedded' : 'link';
  const completed = !!day.completed;
  const guideLines = (day.embedded_guide || '').split('\n').filter(Boolean);

  // Iter. 411.d — Bug rapporté par Marc Chalifoux : le modal ouvrait un
  // backdrop flou mais aucun contenu visible. Cause identique à Iter. 406.dd
  // (OrionSymbioseModal) — un ancêtre du Dashboard applique un `transform`
  // ou `filter` qui crée un nouveau stacking context et confine ce
  // `fixed inset-0` à la boîte du parent au lieu du viewport, poussant la
  // carte hors de l'écran visible. Solution : createPortal vers
  // `document.body` pour échapper à tout ancêtre.
  const overlay = (
    <div
      className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ height: '100dvh' }}
      onClick={onClose}
      data-testid="weekly-quest-day-modal"
    >
      <div
        className="bg-card border border-amber-400/40 rounded-t-3xl sm:rounded-3xl max-w-md w-full p-5 shadow-2xl max-h-[92dvh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: '0 0 60px rgba(251, 191, 36, 0.18)' }}
      >
        {/* Header with category-colored icon block */}
        <div className="flex items-start gap-3 mb-4">
          <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${tint} border border-white/15 flex items-center justify-center text-3xl shrink-0`} aria-hidden="true">
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              {fr ? `Jour ${day.day_index} / 7` : `Day ${day.day_index} / 7`}
              {completed && (
                <span className="ml-2 text-emerald-300/90 normal-case tracking-normal">
                  · {fr ? 'Validé' : 'Done'} ✓
                </span>
              )}
            </p>
            <h2 className="font-heading text-base uppercase tracking-tight text-foreground leading-tight mt-0.5" data-testid="weekly-quest-modal-title">
              {day.title}
            </h2>
            <p className="text-xs text-muted-foreground/85 mt-1 leading-snug">
              {day.description}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground p-1 shrink-0"
            data-testid="weekly-quest-modal-close"
            aria-label={fr ? 'Fermer' : 'Close'}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Educational description */}
        {day.educational_description && (
          <div className="rounded-2xl bg-amber-500/5 border border-amber-300/20 px-4 py-3 mb-4" data-testid="weekly-quest-modal-edu">
            <p className="text-[10px] uppercase tracking-wider text-amber-300 font-bold mb-1.5 flex items-center gap-1.5">
              <BookOpen className="w-3 h-3" />
              {fr ? 'Pourquoi c\'est important' : 'Why it matters'}
            </p>
            <p className="text-xs text-foreground/90 leading-relaxed">
              {day.educational_description}
            </p>
          </div>
        )}

        {/* Action area — depends on action_type */}
        {actionType === 'embedded' ? (
          <div className="space-y-3" data-testid="weekly-quest-modal-embedded">
            {guideLines.length > 0 && (
              <div className="rounded-2xl bg-secondary/40 border border-border/40 px-4 py-3">
                <p className="text-[10px] uppercase tracking-wider text-cyan-300 font-bold mb-2">
                  {fr ? 'Comment faire' : 'How to'}
                </p>
                <ol className="space-y-1.5 list-decimal list-inside marker:text-cyan-300/70">
                  {guideLines.map((line, idx) => {
                    // Strip leading numbering ("1. ", "2. ") because we use list-decimal already
                    const clean = line.replace(/^\s*\d+[.)]\s*/, '');
                    return (
                      <li key={idx} className="text-xs text-foreground/90 leading-snug pl-1">
                        {clean}
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}
            <button
              type="button"
              onClick={onMarkComplete}
              disabled={completed || pending}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-amber-400 hover:bg-amber-300 text-amber-950 font-bold uppercase tracking-wider text-xs disabled:opacity-60 disabled:cursor-not-allowed active:scale-95 transition-all"
              data-testid="weekly-quest-modal-mark-complete"
            >
              {pending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {completed
                ? (fr ? 'Déjà complété' : 'Already done')
                : (fr ? `Marquer comme complété (+25 pts)` : `Mark as completed (+25 pts)`)}
            </button>
          </div>
        ) : (
          <div className="space-y-2" data-testid="weekly-quest-modal-link">
            <button
              type="button"
              onClick={() => onLink(day.link_target)}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-amber-400 hover:bg-amber-300 text-amber-950 font-bold uppercase tracking-wider text-xs active:scale-95 transition-all"
              data-testid="weekly-quest-modal-link-btn"
            >
              {day.link_label_fr || (fr ? 'Aller à l\'action' : 'Go to action')}
              <ArrowRight className="w-4 h-4" />
            </button>
            {!completed && (
              <button
                type="button"
                onClick={onMarkComplete}
                disabled={pending}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-amber-400/40 hover:bg-amber-400/10 text-amber-200 font-bold uppercase tracking-wider text-[11px] disabled:opacity-60 disabled:cursor-not-allowed active:scale-95 transition-all"
                data-testid="weekly-quest-modal-mark-anyway"
              >
                {pending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                {fr ? 'Ou marquer directement (+25 pts)' : 'Or mark directly (+25 pts)'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(overlay, document.body);
}
